export type FileDownloadProgress = {
  phase: 'starting' | 'downloading' | 'complete';
  percent: number;
  etaSeconds: number | null;
};

export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, '').trim());
    } catch {
      /* keep looking */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

export function formatRemaining(seconds: number | null): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const s = Math.max(0, Math.round(seconds));
  if (s < 1) return '<1s left';
  if (s < 60) return `${s}s left`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s left` : `${m}m left`;
}

/** Keep the Export pill moving while the server builds the workbook (no bytes yet). */
export function smoothedExportPercent(elapsedMs: number, realPercent: number): number {
  const wait = Math.max(
    1,
    Math.min(90, Math.round(90 * (1 - Math.exp(-Math.max(0, elapsedMs) / 5600)))),
  );
  if (realPercent <= 0) return wait;
  return Math.min(99, Math.max(wait, Math.round(realPercent)));
}

function triggerSave(blob: Blob, filename: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Stream a download Response to a file, reporting percent + remaining time when length is known. */
export async function saveResponseAsFile(
  res: Response,
  fallbackName: string,
  onProgress?: (p: FileDownloadProgress) => void,
): Promise<void> {
  const name = filenameFromDisposition(res.headers.get('content-disposition'), fallbackName);
  const total = Number(res.headers.get('content-length')) || 0;
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const chunks: BlobPart[] = [];
  let received = 0;

  const report = (
    phase: FileDownloadProgress['phase'],
    percent: number,
    etaSeconds: number | null,
  ) => {
    onProgress?.({ phase, percent, etaSeconds });
  };

  const body = res.body;
  if (body && typeof body.getReader === 'function') {
    const reader = body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      chunks.push(value);
      received += value.byteLength;
      const elapsedSec =
        ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - started) / 1000;
      const percent =
        total > 0
          ? Math.min(99, Math.round((received / total) * 100))
          : smoothedExportPercent(elapsedSec * 1000, 0);
      const eta =
        total > 0 && elapsedSec > 0.15 && received > 0
          ? (total - received) / (received / elapsedSec)
          : null;
      report('downloading', percent, eta);
    }
    triggerSave(new Blob(chunks, { type: res.headers.get('content-type') || undefined }), name);
  } else {
    const blob = await res.blob();
    triggerSave(blob, name);
  }
  report('complete', 100, 0);
}
